/*
 * Slideshow for the media half of the image-with-text section.
 *
 * One slide at a time, sliding horizontally. Each slide is a link out, so the markup stays a list
 * of anchors and this only moves the track and keeps the controls in sync — nothing here is
 * required for the links to work, and with JS off the first slide still shows and still links.
 */
class ImageTextSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slideshow-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.dots = Array.from(this.querySelectorAll('[data-slideshow-dot]'));
    this.index = 0;

    // A single slide is just an image. Drop the controls rather than show a slideshow of one.
    if (this.slides.length < 2) {
      this.querySelectorAll('[data-slideshow-controls], [data-slideshow-dots]').forEach((el) => el.remove());
      return;
    }

    this.interval = parseInt(this.dataset.interval, 10) || 5000;

    this.querySelector('[data-slideshow-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-slideshow-next]')?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    // Pause while someone is looking at a slide or tabbing through its link.
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());

    // No point running a timer against a tab nobody is looking at.
    this.visibilityHandler = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.go(0);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  start() {
    // Reduced motion keeps the arrows and dots — it only stops the slideshow moving by itself.
    if (this.prefersReducedMotion || document.hidden) return;
    this.stop();
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  step(direction) {
    this.go(this.index + direction);
  }

  go(next) {
    const count = this.slides.length;
    this.index = ((next % count) + count) % count;

    this.track.style.transform = `translateX(-${this.index * 100}%)`;

    this.slides.forEach((slide, i) => {
      const current = i === this.index;
      slide.toggleAttribute('inert', !current);
      slide.setAttribute('aria-hidden', String(!current));
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      if (i === this.index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }
}

customElements.define('image-text-slideshow', ImageTextSlideshow);
